import React, { FC, useMemo, useState, useEffect } from "react";
import { useFormik } from "formik";

import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { faPenToSquare } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface Permission {
  uuid: string;
  subgroup: string | null;
  group: string | null;
  description: string;
}

export interface Role {
  uuid?: string;
  name: string;
  permissions: Permission[];
  specialistType?: number;
  baseRole?: string;
}

export type RolesTableProps = {
  permissions: Permission[];
  roles: Role[];
  onRevoke: (
    role: Role,
    permission: Permission,
    group?: string,
    subgroup?: string
  ) => void;
  onAssign: (
    role: Role,
    permission: Permission,
    group?: string,
    subgroup?: string
  ) => void;
  editRole: (role: Role) => void;
};

export const RolesTable: FC<RolesTableProps> = ({
  permissions,
  roles,
  onRevoke,
  onAssign,
  editRole,
}) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [openSubgroups, setOpenSubgroups] = useState<Record<string, boolean>>(
    {}
  );

  const groups = useMemo(() => {
    return Object.groupBy(
      permissions,
      (permission: Permission) => permission.group ?? ""
    );
  }, [permissions]);

  const subgroups = useMemo(() => {
    return Object.entries(groups).reduce(
      (acc, [group, perms]) => {
        acc[group] = Object.groupBy(
          perms,
          (perm: Permission) => perm.subgroup ?? ""
        );
        return acc;
      },
      {} as Record<string, Record<string, Permission[]>>
    );
  }, [groups]);

  // Función para generar clave única para subgrupos
  const getSubgroupKey = (group: string, subgroup: string) => {
    return `${group}__${subgroup}`;
  };

  useEffect(() => {
    const initialOpenGroups = Object.keys(groups).reduce(
      (acc, group) => {
        acc[group] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setOpenGroups(initialOpenGroups);

    const initialOpenSubgroups = Object.entries(subgroups).reduce(
      (acc, [group, subgroups]) => {
        Object.keys(subgroups).forEach((subgroup) => {
          const subgroupKey = getSubgroupKey(group, subgroup);
          acc[subgroupKey] = true;
        });
        return acc;
      },
      {} as Record<string, boolean>
    );
    setOpenSubgroups(initialOpenSubgroups);
  }, [groups, subgroups]);

  const toggleGroup = (group: string) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const toggleSubgroup = (group: string, subgroup: string) => {
    const subgroupKey = getSubgroupKey(group, subgroup);
    setOpenSubgroups((prev) => ({
      ...prev,
      [subgroupKey]: !prev[subgroupKey],
    }));
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: roles.reduce(
      (values: Record<string, string[]>, role: Role) => ({
        ...values,
        [role.name]: role.permissions.map((permission) => permission.uuid),
      }),
      {}
    ),
    onSubmit: () => {},
  });

  return (
    <>
      <div className="overflow-x-auto">
        <div className="min-w-full shadow-sm rounded-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-800 uppercase tracking-wider w-60 pl-10 pr-10 md:sticky left-0 top-0 bg-gray-100 z-[2]">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-sm">Permiso</span>
                  </div>
                </th>
                {roles.map((role) => (
                  <th
                    key={role.name}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-800 uppercase tracking-wider bg-gray-50 md:sticky top-0"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span className="text-sm">{role.name}</span>
                      <button
                        onClick={() => editRole(role)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(subgroups).map(([group, subgroups]) => (
                <React.Fragment key={group}>
                  <tr>
                    <td className="px-3 py-3 text-left text-sm font-medium uppercase tracking-wider md:sticky left-0 bg-gray-50">
                      <button
                        onClick={() => toggleGroup(group)}
                        className="w-full text-left flex justify-start space-x-3"
                      >
                        <span>
                          <FontAwesomeIcon
                            icon={
                              openGroups[group] ? faChevronUp : faChevronDown
                            }
                          />
                        </span>
                        <span>{group || "Generales"}</span>
                      </button>
                    </td>
                    <td colSpan={roles.length - 1}></td>
                  </tr>
                  {openGroups[group] &&
                    Object.entries(subgroups).map(([subgroup, permissions]) => {
                      const subgroupKey = getSubgroupKey(group, subgroup);
                      return (
                        <React.Fragment key={subgroupKey}>
                          <tr>
                            <td className="px-10 py-3 text-left text-sm font-medium uppercase tracking-wider md:sticky left-0 bg-gray-50">
                              <button
                                onClick={() => toggleSubgroup(group, subgroup)}
                                className="w-full text-left flex justify-start"
                              >
                                <span>
                                  <FontAwesomeIcon
                                    icon={
                                      openSubgroups[subgroupKey]
                                        ? faChevronUp
                                        : faChevronDown
                                    }
                                  />
                                </span>
                                <span className="ml-3">
                                  {subgroup || "General"}
                                </span>
                              </button>
                            </td>
                            <td colSpan={roles.length - 1}></td>
                          </tr>
                          {openSubgroups[subgroupKey] &&
                            permissions.map((permission) => (
                              <tr key={permission.uuid}>
                                <td className="px-6 py-4 text-sm md:sticky left-0 bg-gray-50">
                                  <div className="ml-11 w-56">
                                    {permission.description}
                                  </div>
                                </td>
                                {roles.map((role) => (
                                  <td
                                    key={role.name}
                                    className="px-6 py-4 whitespace-nowrap text-sm"
                                  >
                                    <div className="flex items-center justify-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded gray-300 text-primary focus:ring-primary"
                                        id={permission.uuid}
                                        checked={
                                          !!formik.values[role.name]?.find(
                                            (uuid: string) =>
                                              uuid === permission.uuid
                                          )
                                        }
                                        onChange={(event) => {
                                          if (event.target.checked) {
                                            onAssign(
                                              role,
                                              permission,
                                              group,
                                              subgroup
                                            );
                                          } else {
                                            onRevoke(
                                              role,
                                              permission,
                                              group,
                                              subgroup
                                            );
                                          }
                                        }}
                                      />
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
